import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { uniqueId } from 'lodash';
import { Plus, Minus } from 'lucide-react';
import AppLayout from '../../Layouts/AppLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { createGroup, updateGroup } from '../../../services/groupsOfPeopleService';
import { useAlert } from '../../../hooks/useAlert';


const PERSON_DEFAULT_STATE = {
  key: uniqueId('person'),
  name: '',
  dni: '',
  phone: '',
  email: '',
  address: ''
};

const GroupForm = ({ mode }) => {
  const [groupName, setGroupName] = useState('');
  const [groupDescription, setGroupDescription] = useState('');
  const [people, setPeople] = useState([PERSON_DEFAULT_STATE]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const group = location.state?.group
  const { successAlert, errorAlert } = useAlert();

  useEffect(() => {
    setGroupName(group?.name || '');
    setGroupDescription(group?.description || '');
    // Preload persons if available
    if (group?.people) {
      setPeople(group.people);
    }
  }, [mode, group]);

  const handleSave = () => {
    setLoading(true);
    if (mode === 'create') {
      createGroup({
        name: groupName,
        description: groupDescription,
        persons: people
      }).then(() => {
        successAlert({ message: 'Grupo creado con éxito' });
        navigate('/grupo-personas');
      }).catch(err => {
        errorAlert({
          title: 'Error al crear grupo',
          exception: err
        });
      }).finally(() => {
        setLoading(false);
      });
    } else if (mode === 'edit') {
      updateGroup({
        id: group.id, // assuming group has an "id" property
        name: groupName,
        description: groupDescription,
        persons: people
      }).then(() => {
        successAlert({ message: 'Grupo actualizado con éxito' });
        navigate('/grupo-personas');
      }).catch(err => {
        errorAlert({
          title: 'Error al actualizar grupo',
          exception: err
        });
      }).finally(() => {
        setLoading(false);
      });
    }
  };

  const handleChangePerson = (index, field, value) => {
    const updatedPeople = [...people];
    updatedPeople[index] = { ...updatedPeople[index], [field]: value };
    setPeople(updatedPeople);
  };

  const addPerson = () => {
    setPeople([...people, { ...PERSON_DEFAULT_STATE, key: uniqueId('person') }]);
  };

  const removePerson = (index) => {
    const updated = [...people];
    updated.splice(index, 1);
    setPeople(updated);
  };

  return (
    <AppLayout
      title={mode === 'edit' ? "Editar Grupo" : mode === 'view' ? "Ver Grupo" : "Nuevo Grupo"}
      prevButtonVisible
    >
      <div className="bg-white rounded-md shadow-md p-6">
        <div className="mb-4">
          <Label htmlFor="group-name">Nombre del Grupo</Label>
          <Input
            id="group-name"
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            disabled={mode === 'view'}
          />
        </div>
        <div className="mb-4">
          <Label htmlFor="group-description">Descripción del Grupo</Label>
          <Input
            id="group-description"
            value={groupDescription}
            onChange={(e) => setGroupDescription(e.target.value)}
            disabled={mode === 'view'}
          />
        </div>
        {/* Personas Section */}
        <div className="mb-4">
          <div className="border-b border-red-500 text-center mt-2 mb-4">
            Personas
          </div>
          {people.map((p, index) => (
            <div key={p.key} className="border border-gray-200 p-2 mb-2">
              {/* Nombre */}
              <div className="mb-2">
                <Label>Nombre</Label>
                <Input
                  value={p.name}
                  onChange={(e) => handleChangePerson(index, 'name', e.target.value)}
                  disabled={mode === 'view'}
                />
              </div>
              {/* DNI */}
              <div className="mb-2">
                <Label>DNI</Label>
                <Input
                  value={p.dni}
                  onChange={(e) => handleChangePerson(index, 'dni', e.target.value)}
                  disabled={mode === 'view'}
                />
              </div>
              {/* Telefono */}
              <div className="mb-2">
                <Label>Teléfono</Label>
                <Input
                  value={p.phone}
                  onChange={(e) => handleChangePerson(index, 'phone', e.target.value)}
                  disabled={mode === 'view'}
                />
              </div>
              {/* Email */}
              <div className="mb-2">
                <Label>Email</Label>
                <Input
                  value={p.email}
                  onChange={(e) => handleChangePerson(index, 'email', e.target.value)}
                  disabled={mode === 'view'}
                />
              </div>
              {/* Dirección */}
              <div className="mb-2">
                <Label>Dirección (opcional)</Label>
                <Input
                  value={p.address}
                  onChange={(e) => handleChangePerson(index, 'address', e.target.value)}
                  disabled={mode === 'view'}
                />
              </div>
              {mode !== 'view' && (
                <div className="flex gap-2">
                  {index === people.length - 1 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={addPerson}
                      className="rounded-full p-2"
                    >
                      <Plus className="h-4 w-4 text-blue-500" />
                    </Button>
                  )}
                  {people.length > 1 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => removePerson(index)}
                      className="rounded-full p-2"
                    >
                      <Minus className="h-4 w-4 text-red-500" />
                    </Button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
        {mode !== 'view' && (
          <Button onClick={handleSave} loading={loading}>
            {mode === 'edit' ? "Actualizar" : "Guardar"}
          </Button>
        )}
      </div>
    </AppLayout>
  );
};

export default GroupForm;
